// chrome.tsx — Sidebar (nav) + Topbar (search, store selector, role switcher).
// Ported from the prototype's chrome.jsx; styles unchanged. Navigation now
// drives React Router (the layout maps nav ids to routes), and badges/alerts
// come from props the layout supplies (TanStack Query), not a global store.
import { useState, useEffect, useRef, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './icons';
import type { IconName } from './icons';
import { Logo, Avatar } from './primitives';
import { useStore } from '@/store/store';
import { useLogout } from '@/auth/useLogout';
import { ROLES, BACKEND_ROLE_MAP, hasAllStoreAccess } from '@/auth/rbac';
import type { RoleId } from '@/auth/rbac';
import { useMe } from '@/auth/useMe';
import { STORES } from '@/domain/reference';
import { useStores } from '@/api/useStores';
import { useRealApi } from '@/api/paths';
import { applyTheme, getTheme, type Theme } from '@/styles/theme';
import type { Alert } from '@/domain/types';
import type { AppNotification } from '@/features/stocks/notifications';

export interface NavEntry {
  id: string;
  label: string;
  icon: IconName;
}

export const NAV: NavEntry[] = [
  { id: 'products', label: 'Products', icon: 'products' },
  { id: 'batches', label: 'Batches', icon: 'batches' },
  { id: 'locations', label: 'Locations', icon: 'locations' },
  { id: 'adjustments', label: 'Adjustments', icon: 'adjustments' },
  { id: 'reconciliation', label: 'Reconciliation', icon: 'reconciliation' },
  { id: 'alerts', label: 'Alerts', icon: 'alerts' },
  { id: 'importexport', label: 'Import / Export', icon: 'importexport' },
];

export const MASTER_NAV: NavEntry[] = [
  { id: 'md-products', label: 'Products', icon: 'box' },
  { id: 'md-bins', label: 'Storage Locations', icon: 'locations' },
  { id: 'md-storetypes', label: 'Store Types', icon: 'store' },
  { id: 'md-bintypes', label: 'Storage Location Types', icon: 'layers' },
  { id: 'md-stores', label: 'Stores', icon: 'store' },
  { id: 'md-devices', label: 'Handheld Devices', icon: 'scan' },
  { id: 'md-manufacturers', label: 'Manufacturers', icon: 'truck' },
  { id: 'md-dispense-locations', label: 'Collection Area', icon: 'box' },
  { id: 'md-tray-master', label: 'Basket Master', icon: 'clipboard' },
  { id: 'md-stations', label: 'Stations & Printers', icon: 'scan' },
  { id: 'md-labels', label: 'Labels & Barcodes', icon: 'barcode' },
  { id: 'md-policies', label: 'Policies', icon: 'sliders' },
  { id: 'md-workflow', label: 'Dispensing Workflow', icon: 'layers' },
  { id: 'md-rbac', label: 'Roles & Permissions', icon: 'shield' },
  { id: 'md-modulesettings', label: 'Module Settings', icon: 'settings' },
  { id: 'md-import', label: 'Import / Export', icon: 'importexport' },
];

export const RX_NAV: NavEntry[] = [
  // No separate prescription queue — everything lives in the Dispensing → Dispense Queue.
  { id: 'rx-patients', label: 'Patient Search', icon: 'user' },
  { id: 'rx-manual', label: 'Create Prescription', icon: 'edit' },
];

export const MST_NAV: NavEntry[] = [
  { id: 'mst-stock', label: 'Network Stock', icon: 'layers' },
  { id: 'mst-overstock', label: 'Overstock', icon: 'box' },
  { id: 'mst-requests', label: 'Transfer Requests', icon: 'multistore' },
  { id: 'mst-tracking', label: 'Transfer Tracking', icon: 'truck' },
  { id: 'mst-discrepancies', label: 'Discrepancies', icon: 'alerts' },
  { id: 'mst-history', label: 'History & Export', icon: 'history' },
];

export const COMPLIANCE_NAV: NavEntry[] = [
  { id: 'compliance', label: 'Audit Log Explorer', icon: 'filecheck' },
];

export const STK_NAV: NavEntry[] = [
  { id: 'stk-inward', label: 'Inward Entries', icon: 'truck' },
  { id: 'stk-transfers', label: 'Transfers', icon: 'multistore' },
  { id: 'stk-adjustments', label: 'Adjustments', icon: 'adjustments' },
  { id: 'stk-returns', label: 'Returns & Recalls', icon: 'refresh' },
  { id: 'stk-otc', label: 'OTC Sale', icon: 'store' },
  { id: 'stk-import', label: 'Import / Scan', icon: 'scan' },
  { id: 'stk-reports', label: 'Alerts & Reports', icon: 'fileText' },
];

// Dispensing (stock outward) — own top-level module; pages are tabs in the hub.
export const DISPENSING_NAV: NavEntry[] = [
  // The single unified queue: every prescription, from arrival to posted, on one adjustable pipeline.
  { id: 'disp-queue', label: 'Dispense Queue', icon: 'pill' },
  { id: 'disp-trays', label: 'Basket Manager', icon: 'clipboard' },
  { id: 'disp-capacity', label: 'Basket Capacity', icon: 'box' },
  { id: 'disp-locations', label: 'Collection Area', icon: 'locations' },
  { id: 'disp-backorders', label: 'Backorders', icon: 'batches' },
  { id: 'disp-partpacks', label: 'Part-Packs', icon: 'box' },
];

function NavItem({
  item,
  active,
  badge,
  onClick,
  rail = false,
}: {
  item: NavEntry;
  active: boolean;
  badge?: number;
  onClick: () => void;
  rail?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={rail ? item.label : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: rail ? 'center' : 'flex-start',
        gap: rail ? 0 : 12,
        height: 42,
        padding: rail ? 0 : '0 14px',
        position: 'relative',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        fontSize: 'var(--fs-emphasis)',
        fontWeight: 600,
        color: active
          ? 'var(--primary-contrast)'
          : hover
            ? 'var(--on-dark-text-high)'
            : 'var(--on-dark-text-dim)',
        background: active
          ? 'var(--chrome-active-bg)'
          : hover
            ? 'var(--chrome-bg-hover)'
            : 'transparent',
        boxShadow: active ? 'inset 3px 0 0 var(--primary-light)' : 'none',
        transition: 'all var(--duration-fast) var(--ease)',
      }}
    >
      <Icon name={item.icon} size={20} />
      {rail && badge ? (
        <span
          style={{
            position: 'absolute',
            top: 6,
            right: 10,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--error-solid)',
          }}
        />
      ) : null}
      {!rail && <span>{item.label}</span>}
      {!rail && badge ? (
        <span
          style={{
            marginLeft: 'auto',
            background: 'var(--error-solid)',
            color: 'var(--primary-contrast)',
            fontSize: 'var(--fs-caption)',
            fontWeight: 700,
            borderRadius: 'var(--radius-pill)',
            padding: '1px 7px',
          }}
        >
          {badge}
        </span>
      ) : null}
    </div>
  );
}

function NavGroup({
  label,
  items,
  active,
  onNav,
  badges = {},
  defaultOpen = false,
  locked = false,
  dashId,
  rail = false,
  groupIcon,
}: {
  label: string;
  items: NavEntry[];
  active: string;
  onNav: (id: string) => void;
  badges?: Record<string, number>;
  defaultOpen?: boolean;
  locked?: boolean;
  dashId?: string;
  rail?: boolean;
  groupIcon?: IconName;
}) {
  // open/closed persists per module across refreshes. With NO saved preference, only the module the
  // user is currently in starts open — the rest collapse so the rail isn't overwhelming on first load.
  const storageKey = `piq.ui.nav.${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  // Entries that aren't just the group's own dashboard. A group whose ONLY
  // entry is its dashboard (Audit & Compliance) renders as a flat link —
  // a one-item dropdown is chrome, not navigation.
  const childItems = items.filter((n) => n.id !== dashId);
  const isActiveGroup = (!!dashId && active === dashId) || items.some((n) => n.id === active);
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) return saved === '1';
    } catch {
      /* tests */
    }
    return isActiveGroup || defaultOpen;
  });
  // Auto-open a group when the user navigates INTO it (transition only, so a manual collapse while
  // you stay in the module is respected).
  const wasActive = useRef(isActiveGroup);
  useEffect(() => {
    if (isActiveGroup && !wasActive.current) setOpen(true);
    wasActive.current = isActiveGroup;
  }, [isActiveGroup]);
  const toggleOpen = () => {
    // side effect outside the updater — StrictMode double-invokes updaters
    const next = !open;
    try {
      localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      /* tests */
    }
    setOpen(next);
  };
  const [hover, setHover] = useState(false);
  const dashActive = dashId && active === dashId;
  // a collapsed group still shows where you are when a child page is active
  const childActive = childItems.some((n) => n.id === active);
  const headerActive = dashActive || (!open && childActive);
  if (rail) {
    return (
      <div style={{ marginBottom: 6 }}>
        <div style={{ height: 1, background: 'var(--on-dark-wash)', margin: '0 8px 8px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {dashId && groupIcon && (
            <NavItem
              rail
              item={{ id: dashId, label: `${label} — Dashboard`, icon: groupIcon }}
              active={!!dashActive}
              onClick={() => !locked && onNav(dashId)}
            />
          )}
          {!locked &&
            childItems.map((n) => (
              <NavItem
                key={n.id}
                rail
                item={n}
                active={active === n.id}
                badge={badges[n.id]}
                onClick={() => onNav(n.id)}
              />
            ))}
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 'var(--space-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 0 8px' }}>
        {childItems.length > 0 ? (
          <button
            onClick={toggleOpen}
            title={open ? 'Collapse' : 'Expand'}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 2,
              display: 'inline-flex',
              color: 'var(--on-dark-text-dim)',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                transition: 'transform var(--duration-fast) var(--ease)',
                transform: open ? 'none' : 'rotate(-90deg)',
              }}
            >
              <Icon name="chevdown" size={14} />
            </span>
          </button>
        ) : (
          <span aria-hidden style={{ width: 18, flex: 'none' }} />
        )}
        <button
          onClick={() => dashId && onNav(dashId)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            flex: 1,
            border: 'none',
            background: headerActive
              ? 'var(--chrome-active-bg)'
              : hover && dashId
                ? 'var(--chrome-bg-hover)'
                : 'transparent',
            cursor: dashId ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            borderRadius: 'var(--radius-sm)',
            padding: '5px 8px',
            fontSize: 'var(--fs-caption)',
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            // -dim keeps the muted look while clearing WCAG AA 4.5:1 on the
            // chrome base and its hover wash in BOTH themes: .55 on the light
            // chrome (#201a26), lifted to .62 on the dark elevated chrome
            // (see the dark block in colors_and_type.css).
            color: headerActive ? 'var(--primary-contrast)' : 'var(--on-dark-text-dim)',
            boxShadow: headerActive ? 'inset 3px 0 0 var(--primary-light)' : 'none',
            transition: 'all var(--duration-fast) var(--ease)',
          }}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
          {locked && <Icon name="lock" size={12} />}
        </button>
      </div>
      {open &&
        childItems.length > 0 &&
        (locked ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: '8px 14px',
              margin: '0 0 2px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--on-dark-wash-subtle)',
              color: 'var(--on-dark-text-faint)',
              fontSize: 'var(--fs-body-sm)',
              fontWeight: 600,
            }}
          >
            <Icon name="lock" size={14} /> Access restricted
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {childItems.map((n) => (
              <NavItem
                key={n.id}
                item={n}
                active={active === n.id}
                badge={badges[n.id]}
                onClick={() => onNav(n.id)}
              />
            ))}
          </div>
        ))}
    </div>
  );
}

/** User / account block pinned to the sidebar's bottom corner. Opens an
 *  upward popover (fixed-position so the sidebar's overflow can't clip it)
 *  with the demo role switcher, profile links and sign-out. */
function SidebarUserMenu({ onNav, rail }: { onNav: (id: string) => void; rail: boolean }) {
  const { me, role, roleName, setRole, avatarUrl } = useStore();
  const logout = useLogout();
  const { data: meData } = useMe();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(getTheme);
  const ref = useRef<HTMLDivElement>(null);

  // Determine which roles to show: real backend roles if available, all roles otherwise
  const availableRoles = useMemo(() => {
    if (meData?.roles?.length) {
      const mapped = meData.roles
        .map((code) => BACKEND_ROLE_MAP[code.toUpperCase()])
        .filter(Boolean) as RoleId[];
      // Show only the roles the user actually has
      return [...new Set(mapped)].map((id) => ROLES[id]).filter(Boolean);
    }
    // Fallback: show all roles (demo mode)
    return Object.values(ROLES);
  }, [meData]);

  const isRealRoles = !!meData?.roles?.length;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const id = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const item: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: 'var(--fs-body)',
    fontWeight: 600,
    color: 'var(--text-primary)',
  };

  return (
    <div ref={ref}>
      <div
        role="button"
        tabIndex={0}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        title={rail ? `${me} · ${roleName}` : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: rail ? 'center' : 'flex-start',
          gap: 10,
          padding: rail ? '8px 0' : '8px 10px',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          background: open ? 'var(--on-dark-wash)' : 'transparent',
          transition: 'background var(--duration-fast) var(--ease)',
        }}
      >
        <Avatar size={34} name={me} src={avatarUrl || undefined} />
        {!rail && (
          <>
            <div style={{ minWidth: 0, flex: 1, lineHeight: 1.25 }}>
              <div
                style={{
                  fontSize: 'var(--fs-body)',
                  fontWeight: 700,
                  color: 'var(--primary-contrast)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {me}
              </div>
              <div
                style={{
                  fontSize: 'var(--fs-caption)',
                  color: 'var(--on-dark-text-dim)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {roleName}
              </div>
            </div>
            <Icon name="chevdown" size={14} color="var(--on-dark-text-dim)" />
          </>
        )}
      </div>

      {open && (
        <div
          style={{
            position: 'fixed',
            left: rail ? 76 : 14,
            bottom: 16,
            width: 248,
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-dropdown)',
            zIndex: 1200,
            overflow: 'hidden',
            animation: 'pop .15s ease',
          }}
        >
          <div style={popHead}>{me}</div>
          <div
            style={{
              padding: '8px 12px 2px',
              fontSize: 'var(--fs-caption)',
              fontWeight: 700,
              color: 'var(--text-disabled)',
              letterSpacing: 0.4,
            }}
          >
            {isRealRoles ? 'YOUR ROLES' : 'SWITCH ROLE (DEMO)'}
          </div>
          <div
            style={{
              padding: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {availableRoles.map((r) => (
              <div
                key={r.id}
                onClick={() => {
                  setRole(r.id);
                  setOpen(false);
                }}
                style={{
                  ...item,
                  padding: '7px 12px',
                  background: r.id === role ? 'var(--primary-lighter)' : 'transparent',
                  color: r.id === role ? 'var(--primary-dark)' : 'var(--text-primary)',
                }}
              >
                <span
                  style={{
                    width: 26,
                    fontSize: 'var(--fs-caption)',
                    fontWeight: 700,
                    color: r.id === role ? 'var(--primary-ink)' : 'var(--text-disabled)',
                  }}
                >
                  {r.short}
                </span>
                {r.name}
              </div>
            ))}
          </div>
          <div style={{ height: 1, background: 'var(--divider)' }} />
          <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div
              onClick={() => {
                onNav('__profile');
                setOpen(false);
              }}
              style={item}
            >
              <Icon name="user" size={17} color="var(--text-secondary)" /> Profile
            </div>
            <div
              onClick={() => {
                onNav('__account');
                setOpen(false);
              }}
              style={item}
            >
              <Icon name="settings" size={17} color="var(--text-secondary)" /> Account Settings
            </div>
          </div>
          <div style={{ height: 1, background: 'var(--divider)' }} />
          <div
            style={{
              padding: '8px 12px 2px',
              fontSize: 'var(--fs-caption)',
              fontWeight: 700,
              // -secondary, not -disabled: this label must clear AA on the
              // popover card in both themes (disabled is 2.9:1 in dark).
              color: 'var(--text-secondary)',
              letterSpacing: 0.4,
            }}
          >
            APPEARANCE
          </div>
          <div
            role="radiogroup"
            aria-label="Appearance"
            style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {(
              [
                { id: 'light', name: 'Light', icon: 'sun' },
                { id: 'dark', name: 'Dark', icon: 'moon' },
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={theme === t.id}
                onClick={() => {
                  // Keep the menu open: the theme flips live, so the user sees
                  // the result (and can flip back) without reopening the menu.
                  applyTheme(t.id);
                  setTheme(t.id);
                }}
                style={{
                  ...item,
                  border: 'none',
                  width: '100%',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  background: theme === t.id ? 'var(--primary-lighter)' : 'transparent',
                  color: theme === t.id ? 'var(--primary-dark)' : 'var(--text-primary)',
                }}
              >
                <Icon
                  name={t.icon}
                  size={17}
                  color={theme === t.id ? 'var(--primary-ink)' : 'var(--text-secondary)'}
                />
                {t.name}
              </button>
            ))}
          </div>
          <div style={{ height: 1, background: 'var(--divider)' }} />
          <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div onClick={logout} style={{ ...item, color: 'var(--error-dark)' }}>
              <Icon name="logout" size={17} color="var(--error-dark)" /> Sign Out
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Sidebar({
  active,
  onNav,
  alertBadge = 0,
  rxBadge = 0,
  rail = false,
  onToggleRail,
}: {
  active: string;
  onNav: (id: string) => void;
  alertBadge?: number;
  /** prescriptions awaiting clinical check */
  rxBadge?: number;
  /** icon-only rail mode (user toggle, persisted by the shell) */
  rail?: boolean;
  onToggleRail?: () => void;
}) {
  const { can } = useStore();
  return (
    <aside
      style={{
        width: rail ? 68 : 256,
        flex: 'none',
        background: 'var(--chrome-bg)',
        height: '100%',
        boxSizing: 'border-box',
        padding: rail ? '22px 8px' : '22px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: rail ? 12 : 20,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'width var(--duration-base) var(--ease)',
      }}
    >
      <div
        style={{
          padding: rail ? 0 : '0 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: rail ? 'center' : 'flex-start',
          gap: 'var(--space-2)',
        }}
      >
        {rail ? (
          <span
            title="PharmacyIQ"
            style={{
              fontWeight: 700,
              fontSize: 'var(--fs-emphasis)',
              color: 'var(--primary-contrast)',
              border: '1.6px solid var(--primary-light)',
              borderRadius: 'var(--radius-sm)',
              padding: '1px 6px',
            }}
          >
            IQ
          </span>
        ) : (
          <Logo dark />
        )}
      </div>
      <div
        style={{
          padding: rail ? 0 : '0 10px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: rail ? 0 : 18,
        }}
      >
        <NavGroup
          rail={rail}
          groupIcon="batches"
          label="Inventory Management"
          items={NAV}
          active={active}
          onNav={onNav}
          badges={{ alerts: alertBadge }}
          dashId="dashboard"
        />
        <NavGroup
          rail={rail}
          groupIcon="truck"
          label="STOCKS"
          items={STK_NAV}
          active={active}
          onNav={onNav}
          dashId="stk-dashboard"
        />
        <NavGroup
          rail={rail}
          groupIcon="pill"
          label="Dispensing"
          items={DISPENSING_NAV}
          active={active}
          onNav={onNav}
          dashId="disp-home"
        />
        {/* Prescriptions module is hidden — Create Prescription now lives in the Dispense Queue. */}
        <NavGroup
          rail={rail}
          groupIcon="multistore"
          label="Multi-Store"
          items={MST_NAV}
          active={active}
          onNav={onNav}
          dashId="mst-dashboard"
        />
        <NavGroup
          rail={rail}
          groupIcon="filecheck"
          label="Audit & Compliance"
          items={COMPLIANCE_NAV}
          active={active}
          onNav={onNav}
          locked={!can('audit.read')}
          dashId="compliance"
        />
        <NavGroup
          rail={rail}
          groupIcon="settings"
          label="Master Data"
          items={MASTER_NAV}
          active={active}
          onNav={onNav}
          locked={!can('master.access')}
          dashId="md-home"
        />
      </div>
      <SidebarUserMenu onNav={onNav} rail={rail} />
      <button
        onClick={onToggleRail}
        title={rail ? 'Expand navigation' : 'Collapse navigation'}
        style={{
          border: '1px solid var(--on-dark-border)',
          background: 'var(--on-dark-wash-subtle)',
          color: 'var(--on-dark-text)',
          borderRadius: 'var(--radius-md)',
          height: 34,
          display: onToggleRail ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          cursor: 'pointer',
          fontSize: 'var(--fs-body-sm)',
          fontWeight: 600,
        }}
      >
        <Icon name={rail ? 'chevright' : 'chevleft'} size={15} />
        {!rail && 'Collapse'}
      </button>
      {!rail && (
        <div
          style={{
            padding: '12px',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--on-dark-wash-subtle)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              color: 'var(--on-dark-text-dim)',
              fontSize: 'var(--fs-body-sm)',
              fontWeight: 600,
              marginBottom: 'var(--space-2)',
            }}
          >
            <Icon name="shield" size={15} /> Compliance
          </div>
          <div
            style={{
              fontSize: 'var(--fs-caption)',
              color: 'var(--on-dark-text-dim)',
              lineHeight: 1.5,
            }}
          >
            MHRA Annex 11 · EU GDP · GDPR
            <br />
            Append-only movement ledger active
          </div>
        </div>
      )}
    </aside>
  );
}

/* ---- topbar popovers ---- */
export function Popover({
  open,
  onClose,
  children,
  width = 300,
  align = 'right',
}: {
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  width?: number;
  align?: 'left' | 'right';
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !onClose) return;
    const handler = (e: MouseEvent) => {
      // Close only if the click target is outside the popover panel
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay attaching so the opening click doesn't immediately close
    const id = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: 'calc(100% + 10px)',
        [align]: 0,
        width,
        background: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-dropdown)',
        zIndex: 9999,
        animation: 'pop .15s ease',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

export function Topbar({
  onNav,
  alerts = [],
  onAckAlert,
  notifications = [],
  onMarkNotifRead,
  onMarkAllNotifsRead,
  onOpenNotification,
  rolesSyncing = false,
  onMenu,
  onOpenSearch,
}: {
  onNav: (id: string) => void;
  alerts?: Alert[];
  /** acknowledge (and so dismiss from the bell) a stock alert */
  onAckAlert?: (alert: Alert) => void;
  /** per-user + general dispensing notifications (bell "Notifications" tab) */
  notifications?: AppNotification[];
  onMarkNotifRead?: (id: string) => void;
  onMarkAllNotifsRead?: () => void;
  /** navigate to a notification's deep-link target (closes the bell) */
  onOpenNotification?: (path: string) => void;
  /** true while the real-API /me roles are still syncing — store repair must wait */
  rolesSyncing?: boolean;
  /** present when the sidebar is collapsed into an overlay (compact viewport) */
  onMenu?: () => void;
  /** opens the ⌘K command palette */
  onOpenSearch?: () => void;
}) {
  const { store, setStore, roles } = useStore();
  // Only all-store roles (ADM/SY/IM → system_admin/inventory_manager) may use the group-wide
  // "All Stores" view; every other role is store-scoped to its assigned stores.
  const allStore = hasAllStoreAccess(roles);
  const [storeOpen, setStoreOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [bellTab, setBellTab] = useState<'notifications' | 'alerts'>('notifications');
  const [searchFocus, setSearchFocus] = useState(false);
  const { data: realStores } = useStores();
  // In real API mode, only use actual stores from the API; never fall back to mock seeds
  const stores = useRealApi ? (realStores ?? []) : realStores?.length ? realStores : STORES;
  // Repair a stale/foreign selection (a previous account, a removed store, or an out-of-scope
  // store inherited across logins). All-store roles fall back to the group view; store-scoped roles
  // must land on one of THEIR stores — never 'ALL' (which they aren't allowed to use).
  useEffect(() => {
    // Repairing before the real roles arrive is a RACE: `allStore` is computed
    // from the locally-persisted role until /me syncs, so an all-store user's
    // 'ALL' selection could be coerced (and persisted!) to the first store on
    // every cold load. Wait for the roles.
    if (!realStores?.length || rolesSyncing) return;
    const inScope = realStores.some((s) => s.id === store);
    if (allStore) {
      if (store !== 'ALL' && !inScope) setStore('ALL');
    } else if (store === 'ALL' || !inScope) {
      setStore(realStores[0].id);
    }
  }, [realStores, store, setStore, allStore, rolesSyncing]);
  const activeStore =
    store === 'ALL' ? 'All Stores' : (stores.find((s) => s.id === store)?.name ?? store);
  const openAlerts = alerts.slice(0, 6);
  const bellCount = notifications.length + alerts.length;

  return (
    <header
      style={{
        height: 72,
        flex: 'none',
        background: 'var(--bg-topbar)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--divider)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 24px',
        position: 'relative',
        zIndex: 30,
      }}
    >
      {onMenu && (
        <button
          onClick={onMenu}
          aria-label="Open navigation"
          style={{
            border: '1px solid var(--divider)',
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-md)',
            width: 40,
            height: 40,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon name="layers" size={18} color="var(--text-primary)" />
        </button>
      )}
      {/* Under 768px this collapses to an icon-sized tap target (see app.css):
          at 375px the full search pill collided with the store selector. It
          still opens the same ⌘K palette. */}
      <div
        className="piq-topbar-search"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flex: 1,
          minWidth: 120,
          maxWidth: 440,
          height: 40,
          background: searchFocus ? 'var(--bg-card)' : 'var(--grey-200)',
          border: `1px solid ${searchFocus ? 'var(--primary-main)' : 'transparent'}`,
          borderRadius: 'var(--radius-md)',
          padding: '0 14px',
          color: 'var(--text-disabled)',
          transition: 'all var(--duration-fast) var(--ease)',
        }}
      >
        <Icon name="search" size={18} />
        <input
          readOnly={!!onOpenSearch}
          onClick={onOpenSearch}
          onFocus={(e) => {
            setSearchFocus(true);
            if (onOpenSearch) {
              e.currentTarget.blur();
              onOpenSearch();
            }
          }}
          onBlur={() => setSearchFocus(false)}
          placeholder="Search product, barcode, batch or category…"
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 'var(--fs-emphasis)',
            color: 'var(--text-primary)',
            flex: 1,
          }}
        />
        <span
          style={{
            fontSize: 'var(--fs-caption)',
            fontWeight: 700,
            // text-secondary, not text-disabled: the hint must meet WCAG AA
            // 4.5:1 on the grey-200 search pill (disabled was 2.52:1).
            color: 'var(--text-secondary)',
            border: '1px solid var(--grey-300)',
            borderRadius: 5,
            padding: '1px 6px',
          }}
        >
          ⌘K
        </span>
      </div>

      <div
        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        {/* Store selector */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setStoreOpen((v) => !v)} style={topBtn}>
            <Icon name="store" size={17} color="var(--primary-main)" />
            <span style={{ fontWeight: 700 }}>{activeStore}</span>
            <Icon name="chevdown" size={15} />
          </button>
          <Popover open={storeOpen} onClose={() => setStoreOpen(false)} width={260}>
            <div style={popHead}>Active Store</div>
            {/* Group-wide view is only for all-store roles (ADM/SY/IM); scoped users pick a store. */}
            {allStore && (
              <StoreOpt
                id="ALL"
                name="All Stores"
                sub="Group-wide view"
                active={store === 'ALL'}
                onClick={() => {
                  setStore('ALL');
                  setStoreOpen(false);
                }}
              />
            )}
            {stores
              .filter((s) => s.active !== false)
              .map((s) => (
                <StoreOpt
                  key={s.id}
                  id={s.id}
                  name={s.name}
                  sub={s.address || s.city || ''}
                  active={store === s.id}
                  onClick={() => {
                    setStore(s.id);
                    setStoreOpen(false);
                  }}
                />
              ))}
          </Popover>
        </div>

        {/* Notifications */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setBellOpen((v) => !v)}
            aria-label="Notifications"
            style={{
              position: 'relative',
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              background: 'var(--grey-200)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="bell" size={20} />
            {bellCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 5,
                  right: 6,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--error-solid)',
                  color: 'var(--primary-contrast)',
                  fontSize: 'var(--fs-caption)',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 0 2px var(--grey-200)',
                }}
              >
                {bellCount > 99 ? '99+' : bellCount}
              </span>
            )}
          </button>
          <Popover open={bellOpen} onClose={() => setBellOpen(false)} width={360}>
            {/* Tab strip: Notifications (dispensing events) vs Alerts (stock) */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--divider)' }}>
              <BellTab
                label="Notifications"
                count={notifications.length}
                active={bellTab === 'notifications'}
                onClick={() => setBellTab('notifications')}
              />
              <BellTab
                label="Alerts"
                count={alerts.length}
                active={bellTab === 'alerts'}
                onClick={() => setBellTab('alerts')}
              />
            </div>

            {bellTab === 'notifications' ? (
              <>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {notifications.length === 0 && (
                    <div style={bellEmpty}>You're all caught up — no new notifications.</div>
                  )}
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => {
                        onMarkNotifRead?.(n.id);
                        // Resupply events land on the backorder register row; other
                        // prescription-linked events open the prescription.
                        if (n.backorderId) {
                          // The register's UI id is the uuid's first 8 chars (see backorderToUi)
                          onOpenNotification?.(
                            `/dispensing/backorders?search=${n.backorderId.slice(0, 8)}`,
                          );
                        } else if (n.prescriptionId) {
                          onOpenNotification?.(`/prescriptions/queue/${n.prescriptionId}`);
                        }
                      }}
                      title={n.backorderId || n.prescriptionId ? 'Open' : 'Mark as read'}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--divider)',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          marginTop: 6,
                          flex: 'none',
                          background: priorityColor(n.priority),
                        }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          className="subtitle2"
                          style={{
                            fontSize: 'var(--fs-body)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <span style={{ flex: 1 }}>{n.title}</span>
                          {n.general && <span style={bellTagGeneral}>General</span>}
                        </div>
                        {n.message && (
                          <div className="caption" style={{ marginTop: 1 }}>
                            {n.message}
                          </div>
                        )}
                        <div className="caption" style={{ marginTop: 2, opacity: 0.7 }}>
                          {relativeTime(n.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {notifications.length > 0 && (
                  <div onClick={() => onMarkAllNotifsRead?.()} style={bellFooter}>
                    Mark all as read
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {alerts.length === 0 && <div style={bellEmpty}>No open stock alerts.</div>}
                  {openAlerts.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => onAckAlert?.(a)}
                      title="Acknowledge & dismiss"
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--divider)',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          marginTop: 6,
                          flex: 'none',
                          background:
                            a.tone === 'error'
                              ? 'var(--error-main)'
                              : a.tone === 'warning'
                                ? 'var(--warning-main)'
                                : 'var(--info-main)',
                        }}
                      />
                      <div>
                        <div className="subtitle2" style={{ fontSize: 'var(--fs-body)' }}>
                          {a.t}
                        </div>
                        <div className="caption" style={{ marginTop: 1 }}>
                          {a.meta}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  onClick={() => {
                    onNav('alerts');
                    setBellOpen(false);
                  }}
                  style={bellFooter}
                >
                  View all alerts
                </div>
              </>
            )}
          </Popover>
        </div>
      </div>
    </header>
  );
}
/** Compact tab button for the notification bell panel. */
function BellTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 'var(--fs-body)',
        fontWeight: 700,
        padding: '12px 10px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        color: active ? 'var(--primary-main)' : 'var(--text-secondary)',
        boxShadow: active ? 'inset 0 -2px 0 var(--primary-main)' : 'none',
      }}
    >
      {label}
      {count > 0 && (
        <span
          style={{
            background: active ? 'var(--primary-lighter)' : 'var(--grey-200)',
            color: active ? 'var(--primary-dark)' : 'var(--text-secondary)',
            borderRadius: 'var(--radius-pill)',
            fontSize: 'var(--fs-caption)',
            fontWeight: 700,
            padding: '1px 7px',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function priorityColor(p: string): string {
  if (p === 'urgent') return 'var(--error-main)';
  if (p === 'high') return 'var(--warning-main)';
  if (p === 'low') return 'var(--grey-400)';
  return 'var(--info-main)';
}

/** Coarse relative time for the bell ("just now", "5m", "2h", "3d"). */
function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const bellEmpty: CSSProperties = {
  padding: '28px 16px',
  textAlign: 'center',
  fontSize: 'var(--fs-body)',
  color: 'var(--text-secondary)',
};

const bellFooter: CSSProperties = {
  padding: '12px 16px',
  textAlign: 'center',
  fontSize: 'var(--fs-body)',
  fontWeight: 700,
  color: 'var(--primary-main)',
  cursor: 'pointer',
};

const bellTagGeneral: CSSProperties = {
  flex: 'none',
  fontSize: 'var(--fs-caption)',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  background: 'var(--grey-200)',
  borderRadius: 'var(--radius-pill)',
  padding: '1px 7px',
};

const topBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  height: 40,
  padding: '0 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--grey-300)',
  background: 'var(--bg-card)',
  cursor: 'pointer',
  fontSize: 'var(--fs-body)',
  color: 'var(--text-primary)',
};
const popHead: CSSProperties = {
  padding: '14px 16px',
  fontSize: 'var(--fs-body-sm)',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  borderBottom: '1px solid var(--divider)',
};
function StoreOpt({
  id,
  name,
  sub,
  active,
  onClick,
}: {
  id: string;
  name: string;
  sub: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 16px',
        cursor: 'pointer',
        background: active ? 'var(--primary-lighter)' : 'transparent',
      }}
    >
      <Icon
        name={id === 'ALL' ? 'layers' : 'store'}
        size={17}
        color={active ? 'var(--primary-main)' : 'var(--text-secondary)'}
      />
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--fs-body)',
            fontWeight: 700,
            color: 'var(--text-primary)',
          }}
        >
          {name}
        </div>
        <div className="caption">{sub}</div>
      </div>
      {active && <Icon name="check" size={16} color="var(--primary-main)" />}
    </div>
  );
}
